import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { BusinessIdea, TaskProgress, NextTaskInfo } from './types';
import { getAllTasks, getTaskStage, getTaskModule } from './content-hierarchy';
import { invalidateBusinessIdeaCache } from './cache';

// Re-export types for convenience
export type { NextTaskInfo, TaskProgress } from './types';

// Function to get Firestore instance
const getFirestoreInstance = () => {
  return getFirestore();
};

/**
 * Save a business idea to Firestore and link it to the user
 */
export const saveBusinessIdea = async (businessIdea: BusinessIdea): Promise<string> => {
  const db = getFirestoreInstance();

  // Generate a new document reference with auto-generated ID
  const businessIdeaRef = doc(collection(db, 'businessIdeas'));
  const businessIdeaId = businessIdeaRef.id;

  const userRef = doc(db, 'users', businessIdea.userId);

  // Save the business idea (ID is available as document ID)
  await setDoc(businessIdeaRef, {
    ...businessIdea,
    updatedAt: new Date().toISOString(),
  });

  // Update the user's projects array
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const userData = userSnap.data();
    const currentProjects = userData.projects || [];

    if (!currentProjects.includes(businessIdeaId)) {
      await updateDoc(userRef, {
        projects: arrayUnion(businessIdeaId),
        updatedAt: new Date(),
      });
    }
  } else {
    // User document doesn't exist, create it with the projects array
    await setDoc(userRef, {
      projects: [businessIdeaId],
      updatedAt: new Date(),
    });
  }

  return businessIdeaId;
};

/**
 * Update an existing business idea in Firestore
 */
export const updateBusinessIdea = async (
  businessIdeaId: string,
  updates: Partial<BusinessIdea>,
): Promise<void> => {
  const db = getFirestoreInstance();
  const businessIdeaRef = doc(db, 'businessIdeas', businessIdeaId);

  await updateDoc(businessIdeaRef, {
    ...updates,
    updatedAt: new Date().toISOString(),
  });
};

/**
 * Get a business idea by ID
 */
export const getBusinessIdea = async (businessIdeaId: string): Promise<BusinessIdea | null> => {
  const db = getFirestoreInstance();
  const businessIdeaRef = doc(db, 'businessIdeas', businessIdeaId);
  const businessIdeaSnap = await getDoc(businessIdeaRef);

  if (businessIdeaSnap.exists()) {
    return businessIdeaSnap.data() as BusinessIdea;
  }

  return null;
};

/**
 * Get all business ideas for a user
 */
export const getUserBusinessIdeas = async (userId: string): Promise<BusinessIdea[]> => {
  const db = getFirestoreInstance();

  // First, get the user's projects array
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    return [];
  }

  const userData = userSnap.data();
  const projectIds = userData.projects || [];

  if (projectIds.length === 0) {
    return [];
  }

  // Then fetch all the business ideas in the user's projects
  const businessIdeas: BusinessIdea[] = [];

  // Firestore has a limit of 10 items in 'in' queries, so we need to batch them
  const batchSize = 10;
  for (let i = 0; i < projectIds.length; i += batchSize) {
    const batch = projectIds.slice(i, i + batchSize);
    const businessIdeasRef = collection(db, 'businessIdeas');
    const q = query(businessIdeasRef, where('__name__', 'in', batch));
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      businessIdeas.push(doc.data() as BusinessIdea);
    });
  }

  return businessIdeas;
};

/**
 * Get the primary business idea for a user (most recent or marked as primary)
 */
export const getPrimaryBusinessIdea = async (userId: string): Promise<BusinessIdea | null> => {
  const businessIdeas = await getUserBusinessIdeas(userId);

  if (businessIdeas.length === 0) {
    return null;
  }

  // Return the most recently updated business idea
  return businessIdeas.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  )[0];
};

/**
 * Remove a business idea from a user's projects (for deletion or access revocation)
 */
export const removeBusinessIdeaFromUser = async (
  userId: string,
  businessIdeaId: string,
): Promise<void> => {
  const db = getFirestoreInstance();
  const userRef = doc(db, 'users', userId);

  await updateDoc(userRef, {
    projects: arrayRemove(businessIdeaId),
    updatedAt: new Date(),
  });
};

/**
 * Add a business idea to a user's projects (for collaboration)
 */
export const addBusinessIdeaToUser = async (
  userId: string,
  businessIdeaId: string,
): Promise<void> => {
  const db = getFirestoreInstance();
  const userRef = doc(db, 'users', userId);

  await updateDoc(userRef, {
    projects: arrayUnion(businessIdeaId),
    updatedAt: new Date(),
  });
};

/**
 * Update task progress for a business idea
 */
export const updateTaskProgress = async (
  businessIdeaId: string,
  taskId: string,
  userId: string,
  status: 'not_started' | 'in_progress' | 'completed',
  data?: any,
): Promise<void> => {
  const db = getFirestoreInstance();
  const businessIdeaRef = doc(db, 'businessIdeas', businessIdeaId);

  // Get current business idea to update progress
  const businessIdeaSnap = await getDoc(businessIdeaRef);
  if (!businessIdeaSnap.exists()) {
    throw new Error('Business idea not found');
  }

  const businessIdea = businessIdeaSnap.data() as BusinessIdea;
  const currentProgress = businessIdea.taskProgress || {};
  const currentCompletedTaskIds = businessIdea.completedTaskIds || [];

  // Update the specific task progress
  const updatedProgress: TaskProgress = {
    taskId,
    userId,
    status,
    updatedAt: new Date(),
  };

  // Only include data if provided
  if (data) {
    updatedProgress.data = data;
  }

  if (status === 'completed') {
    updatedProgress.completedAt = new Date();
  }

  currentProgress[taskId] = updatedProgress;

  // Update completed task IDs array
  let updatedCompletedTaskIds = [...currentCompletedTaskIds];
  if (status === 'completed' && !currentCompletedTaskIds.includes(taskId)) {
    updatedCompletedTaskIds.push(taskId);
  } else if (status !== 'completed' && currentCompletedTaskIds.includes(taskId)) {
    updatedCompletedTaskIds = currentCompletedTaskIds.filter((id) => id !== taskId);
  }

  // Determine the last completed task ID (highest sort order)
  let lastCompletedTaskId = businessIdea.lastCompletedTaskId;
  if (status === 'completed') {
    const allTasks = getAllTasks();
    const currentTask = allTasks.find((task) => task.id === taskId);
    const lastCompletedTask = lastCompletedTaskId
      ? allTasks.find((task) => task.id === lastCompletedTaskId)
      : null;

    if (currentTask && (!lastCompletedTask || currentTask.sort > lastCompletedTask.sort)) {
      lastCompletedTaskId = taskId;
    }
  }

  // Update the business idea document
  await updateDoc(businessIdeaRef, {
    taskProgress: currentProgress,
    completedTaskIds: updatedCompletedTaskIds,
    lastCompletedTaskId,
    updatedAt: new Date().toISOString(),
  });

  invalidateBusinessIdeaCache(userId);
};

/**
 * Get the next tasks for a user based on their progress
 * This function works with already-fetched business idea data to avoid duplicate Firestore calls
 */
export const getNextTasks = (
  businessIdea: BusinessIdea,
  userId: string,
  limit: number = 5,
): NextTaskInfo[] => {
  const completedTaskIds = businessIdea.completedTaskIds || [];
  const taskProgress = businessIdea.taskProgress || {};

  const allTasks = getAllTasks();
  const nextTasks: NextTaskInfo[] = [];

  // Find the next incomplete tasks
  for (const task of allTasks) {
    if (nextTasks.length >= limit) break;

    const isCompleted = completedTaskIds.includes(task.id);
    const userTaskProgress = taskProgress[task.id];

    // Check if this task requires individual completion
    if (task.individualCompletionRequired) {
      // For individual tasks, check if this specific user has completed it
      const userCompleted =
        userTaskProgress &&
        userTaskProgress.userId === userId &&
        userTaskProgress.status === 'completed';

      if (!userCompleted) {
        nextTasks.push({
          taskId: task.id,
          title: task.title,
          description: task.description,
          sort: task.sort,
          stage: getTaskStage(task.id),
          module: getTaskModule(task.id),
          premium: task.premium,
          individualCompletionRequired: task.individualCompletionRequired,
          isCompleted: false,
          isAccessible: true, // TODO: Check premium status
        });
      }
    } else {
      // For team tasks, check if anyone has completed it
      if (!isCompleted) {
        nextTasks.push({
          taskId: task.id,
          title: task.title,
          description: task.description,
          sort: task.sort,
          stage: getTaskStage(task.id),
          module: getTaskModule(task.id),
          premium: task.premium,
          individualCompletionRequired: task.individualCompletionRequired,
          isCompleted: false,
          isAccessible: true, // TODO: Check premium status
        });
      }
    }
  }

  return nextTasks;
};
